import React, { createContext, useContext, useState, ReactNode } from 'react';
import { User, DEMO_USERS, USER_PASSWORDS } from '../types/user';

interface UserContextType {
    user: User | null;
    login: (username: string, password: string) => Promise<boolean>;
    logout: () => void;
    hasPermission: (permission: string) => boolean;
    canAccessStation: (stationName: string) => boolean;
    sidebarCollapsed: boolean;
    setSidebarCollapsed: (collapsed: boolean) => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUser = () => {
    const context = useContext(UserContext);
    if (context === undefined) {
        throw new Error('useUser must be used within a UserProvider');
    }
    return context;
};

interface UserProviderProps {
    children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(false);

    const login = async (username: string, password: string): Promise<boolean> => {
        // 模拟异步登录
        await new Promise(resolve => setTimeout(resolve, 500));

        // 验证用户名和密码
        if (USER_PASSWORDS[username] && USER_PASSWORDS[username] === password) {
            const foundUser = DEMO_USERS.find(u => u.username === username);
            if (foundUser) {
                setUser(foundUser);
                localStorage.setItem('currentUser', JSON.stringify(foundUser));
                return true;
            }
        }
        return false;
    };

    const logout = () => {
        setUser(null);
        localStorage.removeItem('currentUser');
    };

    const hasPermission = (permission: string): boolean => {
        if (!user) return false;
        if (user.permissions.includes('all')) return true;
        return user.permissions.includes(permission);
    };

    const canAccessStation = (stationName: string): boolean => {
        if (!user) return false;
        if (user.stationAccess?.includes('all')) return true;
        return user.stationAccess?.includes(stationName) || false;
    };

    // 初始化时从localStorage恢复用户状态
    React.useEffect(() => {
        const savedUser = localStorage.getItem('currentUser');
        if (savedUser) {
            try {
                setUser(JSON.parse(savedUser));
            } catch (error) {
                localStorage.removeItem('currentUser');
            }
        }
    }, []);

    const value: UserContextType = {
        user,
        login,
        logout,
        hasPermission,
        canAccessStation,
        sidebarCollapsed,
        setSidebarCollapsed
    };

    return (
        <UserContext.Provider value={value}>
            {children}
        </UserContext.Provider>
    );
}; 